import { useEffect, useState } from 'react';

/**
 * Parent-side complement of `useClickToEdit` (child iframe). Subscribes to
 * the window `message` channel and returns the most recently clicked
 * component descriptor — or `null` until the operator clicks anything in
 * the preview.
 *
 * Message shape (must match `useClickToEdit`):
 *   { type: 'design-constructor-select', name: string, variant?: string }
 *
 * The hook intentionally returns the raw schema `name` (technical
 * identifier, e.g. `RipButton`, `StatsRow`) rather than a `displayName` —
 * downstream consumers (SectionEditor, ComponentSection) match on `name`
 * because that's the stable key used in COMPONENTS / ComponentOverrides.
 *
 * `variant` is set when the DS component renders per-variant from one JSX
 * prop (Button → `'primary' | 'glass' | 'accent-gloss' | …`). The parent
 * variant-picker then auto-switches to it so the operator immediately
 * sees the knobs of the kind of element they clicked, instead of always
 * landing on `variants[0]`.
 *
 * `nonce` is bumped on every selection so re-clicking the SAME component
 * still triggers React state updates (would otherwise be filtered out by
 * `useState`'s reference-equality short-circuit).
 *
 * No origin check: the iframe is same-origin (Vite dev) or cross-origin
 * (preview-link), but in both cases the operator opens the Constructor
 * intentionally. We accept any sender — the worst-case payload is the
 * sidebar opening to a section that doesn't exist, which we filter out
 * downstream by checking `findComponent(name)`.
 */
export interface SelectedComponent {
  name: string;
  variant?: string;
  nonce: number;
}

export function useSelectedComponent(): SelectedComponent | null {
  const [selected, setSelected] = useState<SelectedComponent | null>(null);
  useEffect(() => {
    const handler = (e: MessageEvent) => {
      const data = e.data as
        | { type?: unknown; name?: unknown; variant?: unknown }
        | null;
      if (
        typeof data !== 'object' ||
        data === null ||
        data.type !== 'design-constructor-select' ||
        typeof data.name !== 'string'
      ) {
        return;
      }
      const next: SelectedComponent = { name: data.name, nonce: Date.now() };
      if (typeof data.variant === 'string') next.variant = data.variant;
      setSelected(next);
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);
  return selected;
}
